/**
 * Static utility class for percentage calculations and formatting
 */
export class PercentageCalculator {
  /**
   * Calculates percentage from value and total
   * @param value The numerator
   * @param total The denominator
   * @returns Percentage as a number (0-100), or 0 if total is 0
   */
  static calculate(value: number, total: number): number {
    if (total === 0) {
      return 0;
    }
    return (value / total) * 100;
  }

  /**
   * Formats a percentage value with specified decimal places
   * @param value Percentage value
   * @param decimals Number of decimal places (default: 1)
   * @returns Formatted string with % sign
   */
  static format(value: number, decimals: number = 1): string {
    return `${value.toFixed(decimals)}%`;
  }

  /**
   * Calculates and formats percentage in one call
   * @param value The numerator
   * @param total The denominator
   * @param decimals Number of decimal places (default: 1)
   * @returns Formatted percentage string
   */
  static calculateAndFormat(value: number, total: number, decimals: number = 1): string {
    const percentage = this.calculate(value, total);
    return this.format(percentage, decimals);
  }

  /**
   * Ensures percentage value is capped at 100
   * @param value Percentage value
   * @returns Value capped at 100
   */
  static cap(value: number): number {
    return Math.min(value, 100);
  }
}
